import { BbntStockStatus, Cart, Discount } from "../../Common";

export const FREE_DELIVERY_AMOUNT = 150;

/**
 * Store-scoped free-shipping threshold
 * (carriers/freeshipping/free_shipping_subtotal), exposed into
 * window.checkoutConfig by BBNT\FreeFreightIncentive's
 * FreeShippingThresholdProvider. Read at call time — checkoutConfig is
 * rendered by the page and may not exist yet when this module evaluates.
 * Falls back to the legacy hardcoded FREE_DELIVERY_AMOUNT when the config
 * value is unavailable. Display/motivational use only — whether the cart
 * actually gets free shipping is decided by the backend and read from the
 * totals-information response.
 */
export const getFreeDeliveryThreshold = (): number => {
  const configured = Number(
    (window as any)?.checkoutConfig?.free_shipping_subtotal
  );
  if (Number.isFinite(configured) && configured > 0) {
    return configured;
  }
  return FREE_DELIVERY_AMOUNT;
};

export interface CheckoutModel_MyBag extends Cart {}

export interface MyBagPresentationOrder {
  orderValueByItemsBreakdown: {
    orderTotal_withoutDiscountsOrAnything: number;
    saleDiscountsAppliedOnTheOrderTotal: number;
    memberDiscountsAppliedOnTheOrderTotal: number;
    memberDiscountPotential: number;
    allThePromotionalDiscounts: MyBagPresentationOrder_PromotionalDiscount[];
  };
  shippingValueBreakdown?: {
    shippingTotal: number;
    isFreeShipping: boolean;
  };
  giftCardValueBreakdown?: {
    privateBackendCode: string;
    presentableCode: string;
    appliedValue: number;
    postApplicationValueLeftOnTheGiftcard: number;
  }[] | undefined;
  totalValueBreakdown: {
    value: number;
    gst: number;
  };
  areThereAnyGiftcardsInTheOrder: boolean;
  isThisAVirtualOrder: boolean;
  promoCodeBreakdown?: {
    code: string;
  };
}

export interface MyBagPresentationOrder_PromotionalDiscount {
  presentableLabel: string;
  value?: number;
}

export interface MyBagPresentationShippingDetailsOnCart {
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  stateLabel?: string;
  postcode: string;
  country: Uppercase<string>;
  phone: string;
  email: string;
}

export interface MyBagPresentationItem {
  id: string;
  /** Magento product entity id of the line (analytics identity). */
  productId: string;
  /**
   * The quote line as analytics sees it: the server-confirmed quantity and its subtotal —
   * never the quantity a pending or rejected change displays. A gift-card line is presented
   * as one row per card (`howManyOfThisProduct` 1 each, all sharing the line's `id`); these
   * two fields keep the whole line's figures on every row.
   */
  lineQuantity: number;
  lineSubtotal: number;

  title: string;
  distinguishingFeature?: string;
  sku: string;
  howManyOfThisProduct: number;
  url: string;

  itemPrice: number;
  itemPriceNormally: number;

  subtotal: number;
  subtotalNormally: number;

  isMemberPrice: boolean;
  isSalePrice: boolean;

  imageThumbnail?: string;

  currentStockStatus: `${MyBagPresentationItem_StockStatus}`;
  serverErrors: string[];
  isUnavailable: boolean;

  asAGiftcard?: MyBagPresentationItem_AsAGiftcard;
}

export interface MyBagPresentationItem_AsAGiftcard {
  sendTo: string;
}

export enum MyBagPresentationItem_StockStatus {
  IN_STOCK = "IN_STOCK",
  OUT_OF_STOCK = "OUT_OF_STOCK",
  LOW_STOCK = "LOW_STOCK",
}

interface CartItemAvailability {
  bbnt_stock_status?: string | null;
  errors?: unknown;
}

export const getCartItemStockStatus = (
  item: Pick<CartItemAvailability, "bbnt_stock_status">
) => {
  let stockStatus = MyBagPresentationItem_StockStatus.OUT_OF_STOCK;

  const stockStatusFromTheResponse = item.bbnt_stock_status;
  if (stockStatusFromTheResponse) {
    const limitedStock: string[] = [
      BbntStockStatus.LIMITED_STOCK,
      BbntStockStatus.LIMITED_STOCK_ORIGINAL,
    ];
    const isInLimitedStock = limitedStock.includes(stockStatusFromTheResponse);
    if (isInLimitedStock) {
      stockStatus = MyBagPresentationItem_StockStatus.LOW_STOCK;
    }

    const outOfStock: string[] = [
      BbntStockStatus.IN_STORE_ONLY,
      BbntStockStatus.IN_STORE_ONLY_ORIGINAL,
      BbntStockStatus.SOLD_OUT_ONLINE,
      BbntStockStatus.OUT_OF_STOCK,
      BbntStockStatus.COMING_SOON,
    ];
    const isOutOfStock = outOfStock.includes(stockStatusFromTheResponse);
    if (isOutOfStock) {
      stockStatus = MyBagPresentationItem_StockStatus.OUT_OF_STOCK;
    }

    if (!isInLimitedStock && !isOutOfStock) {
      stockStatus = MyBagPresentationItem_StockStatus.IN_STOCK;
    }
  }

  return stockStatus;
};

// The installed resolver returns null for no error and may return [] for has_error.
export const isCartItemUnavailable = (
  item: CartItemAvailability,
  currentStockStatus = getCartItemStockStatus(item)
): boolean =>
  currentStockStatus === MyBagPresentationItem_StockStatus.OUT_OF_STOCK ||
  item.errors != null;

const CheckoutModel_MyBag_Graphql_Request__BillingAddress = `
  billing_address {
    city
    country {
      code
      label
    }
    firstname
    lastname
    postcode
    region {
      code
      label
    }
    street
    telephone
  }
`;

const CheckoutModel_MyBag_Graphql_Request__ShippingAddresses = `
  shipping_addresses {
    firstname
    lastname
    street
    postcode
    city
    region {
      code
      label
    }
    country {
      code
      label
    }
    telephone
    available_shipping_methods {
      amount {
        currency
        value
      }
      available
      carrier_code
      carrier_title
      error_message
      method_code
      method_title
      price_excl_tax {
        value
        currency
      }
      price_incl_tax {
        value
        currency
      }
    }
    selected_shipping_method {
      amount {
        value
        currency
      }
      carrier_code
      carrier_title
      method_code
      method_title
    }
  }
`;

const CheckoutModel_MyBag_Graphql_Request__Items_Prices = `
  prices {
    total_item_discount {
      value
    }
    price {
      value
    }
    row_total {
      value
    }
    discounts {
      label
      amount {
        value
      }
    }
    row_total_including_tax{
      value
    }
  }
`;
const CheckoutModel_MyBag_Graphql_Request__Items_Product_PriceRange = `
  price_range {
    maximum_price {
      regular_price {
        value
      }
      final_price {
        value
      }
      discount {
        amount_off
        percent_off
      }
    }
    minimum_price {
      regular_price {
        value
      }
      final_price {
        value
      }
      discount {
        amount_off
        percent_off
      }
    }
  }
`;
const CheckoutModel_MyBag_Graphql_Request__Items_Product_PriceTiers = `
  price_tiers {
    quantity
    final_price {
      value
    }
    discount {
      amount_off
      percent_off
    }
  }
`;
const CheckoutModel_MyBag_Graphql_Request__Items_Product = `
  product {
    id
    url_rewrites{
      url
    }
    url_prefix
    url_key
    short_description {html}
    name
    cart_description
    item_list_desc
    sku
    special_price
    thumbnail {
      label
      url
    }
    tiered_pricing_available
    
    potential_price_tiers {
      quantity
      final_price {
        value
      }
      discount {
        amount_off
        percent_off
      }
    }

    only_x_left_in_stock
    stock_status

    ${CheckoutModel_MyBag_Graphql_Request__Items_Product_PriceRange}
    ${CheckoutModel_MyBag_Graphql_Request__Items_Product_PriceTiers}
  }
`;

const CheckoutModel_MyBag_Graphql_Request__AppliedCoupons = `
  applied_coupons {
    code
    label
  }
`;

const CheckoutModel_MyBag_Graphql_Request__AppliedGiftCards = `
  applied_gift_cards {
    applied_balance {
      value
      currency
    }
    code
    current_balance {
      value
      currency
    }
    expiration_date
  }
`;

const CheckoutModel_MyBag_Graphql_Request__Prices = `
  prices {
    discounts {
      amount {
        value
      }
      label
    }
    grand_total {
      value
    }
    subtotal_excluding_tax {
      value
    }
    applied_taxes {
      label
      amount {
        value
      }
    }    
  }
`;

export const CheckoutModel_MyBag_Graphql_Request = `
  id
  email

  ${CheckoutModel_MyBag_Graphql_Request__BillingAddress}
  ${CheckoutModel_MyBag_Graphql_Request__ShippingAddresses}
  
  ${CheckoutModel_MyBag_Graphql_Request__AppliedCoupons}
  ${CheckoutModel_MyBag_Graphql_Request__AppliedGiftCards}
  ${CheckoutModel_MyBag_Graphql_Request__Prices}
  
  items {
    id
    bbnt_product_url
    bbnt_stock_status
    quantity
    errors {
      code
      message
    }

    ... on GiftcardCartItem {
      giftcard_attributes {
        giftcard_sender_name
        giftcard_sender_email
        giftcard_recipient_name
        giftcard_recipient_email
        giftcard_message
      }
    }

    ${CheckoutModel_MyBag_Graphql_Request__Items_Prices}

    ${CheckoutModel_MyBag_Graphql_Request__Items_Product}
  }
`;
